import React, { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { Form } from 'react-bootstrap';

import type { IFormInput } from './types';

import { classList, parseInputType } from '@/helpers';
import { media, sizing } from '@/styles/theme';
import { ShowPass } from './ShowPass';
import { Button } from '../Buttons';
import { SIDEBAR_DELAY } from '@/config/variables';
import { Minus, Plus } from '@/assets/icons';
import { InputDescription, InputErrorMessage } from './components';

const Input = React.forwardRef((props: IFormInput, ref): React.ReactElement => {
  const {
    className,
    children,
    id,
    label,
    required,
    register,
    error,
    touched,
    onButtonClick,
    buttonLabel,
    type,
    spinType = 'default',
    isStandalone = false,
    minLength,
    showError,
    ...rest
  } = props;
  const { ...registerData } = register ? register(rest.name) : {};
  const inputRef = useRef<HTMLInputElement | null>(null);

  const isButton = onButtonClick && buttonLabel;

  const displayCustomSpinners = spinType === 'custom' && type === 'number' && !rest.disabled;

  useEffect(() => {
    if (inputRef && rest.autoFocus) {
      inputRef.current?.focus();
    }
  }, [rest.autoFocus]);

  return (
    <Fieldset className={classList(className)}>
      {children}
      <div className={`position-relative mt-1 ${isButton ? 'd-flex' : ''} ${rest.inputwrapperclass || ''}`}>
        {label && (
          <InputLabel htmlFor={id || rest.name}>
            {required && <span className="text-danger me-1">*</span>}
            {label}
          </InputLabel>
        )}
        <div className="flex-grow-1 d-flex">
          <NavButton
            $display={displayCustomSpinners}
            className="item-prev"
            onClick={e => {
              e.preventDefault();
              inputRef.current?.stepDown();
            }}
          />
          <StyledInput
            id={id || rest.name}
            className={classList(error && 'error', rest.name)}
            $spinType={spinType}
            {...parseInputType(type)}
            ref={(element: HTMLInputElement) => {
              ref && ref instanceof Function && ref(element);
              inputRef.current = element;
            }}
            {...registerData}
            {...rest}
            min={minLength}
          />
          <NavButton
            $display={displayCustomSpinners}
            className="item-next"
            onClick={e => {
              e.preventDefault();
              inputRef.current?.stepUp();
            }}
          />
        </div>
        {rest.showpass && <ShowPass inputElementName={rest.name} />}
        {isButton && (
          <GroupedButton onClick={onButtonClick} size="m">
            {buttonLabel}
          </GroupedButton>
        )}
      </div>
      {rest.description && <InputDescription>{rest.description}</InputDescription>}
      {!isStandalone && <InputErrorMessage showError={showError} error={error} touched={touched} />}
    </Fieldset>
  );
});
Input.displayName = 'Input';

export { Input };

const Fieldset = styled.fieldset`
  font-size: ${sizing.fontSize.sm};
  &.form-input {
    .wrapper {
      margin-top: 0 !important;
    }
  }
`;

const InputLabel = styled.label.attrs({
  className: 'mb-1',
})`
  font-size: 16px;
  color: #374151;
  font-weight: 400;
  line-height: 20px;
  padding-bottom: 5px;
`;

const StyledInput = styled(Form.Control).attrs({
  className: 'w-100 px-3 border rounded-3',
})<{ $spinType: 'default' | 'custom' }>`
  background-color: #ffffff;
  color: #343a40;
  height: 48px;
  border-radius: 8px !important;

  &[type='number']::-webkit-inner-spin-button,
  &[type='number']::-webkit-outer-spin-button {
    margin-right: -15px;
    transform: scale(1.2);
  }

  &.form-input {
    &[type='number']::-webkit-inner-spin-button,
    &[type='number']::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  ${({ $spinType }) =>
    $spinType === 'custom' &&
    css`
      &[type='number'] {
        text-align: center;

        border-radius: 0 !important;
        border-right-width: 0 !important;
        border-left-width: 0 !important;

        ${media.laptop} {
          text-align: left;
          border-radius: 8px !important;
          border-right-width: 1px !important;
          border-left-width: 1px !important;
        }

        &::-webkit-inner-spin-button,
        &::-webkit-outer-spin-button {
          opacity: 0 !important;

          ${media.laptop} {
            opacity: revert !important;
            margin-right: -15px;
            transform: scale(1.2);
          }
        }
      }
    `}

  ${media.laptop} {
    border-right-width: 1px !important;
    border-left-width: 1px !important;
    text-align: start;
  }

  &.error {
    border: 1px solid #dc3545 !important;
  }

  &:read-only {
    color: #868e96;
  }
`;

const NavButton = styled.button<{ $display: boolean }>`
  width: 52px;
  height: 48px;
  padding: 0;
  border: 1px solid #ced4da;
  background-color: #fff;
  background-position: center center;
  background-repeat: no-repeat;
  transition: width ${SIDEBAR_DELAY}ms ease-in-out, border-radius ${SIDEBAR_DELAY}ms ease-in-out;
  display: ${({ $display }) => ($display ? 'block' : 'none')};

  ${media.laptop} {
    display: none;
  }

  &:active {
    background-position: 52% 54%;
  }

  &.item-prev {
    background-image: url(${Minus});
    border-radius: 5px 0 0 5px;

    .collapsed & {
      width: 38px;
      border-radius: 5px 5px 0 0;
    }
  }

  &.item-next {
    background-image: url(${Plus});
    border-radius: 0 5px 5px 0;

    .collapsed & {
      width: 38px;
      border-radius: 0 0 5px 5px;
    }
  }
`;

const GroupedButton = styled(Button)`
  background-color: #fff;
  height: 42px;
  margin-left: 10px;
`;
